import { useState, useEffect, useRef, FormEvent } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, getApiUrl } from '../utils/api';
import './CommandCenterPage.css';

interface Order {
    id: string;
    orderNumber: string;
    platform: string;
    customerName: string;
    total: number;
    currency: string;
    status: string;
    needsAction: boolean;
    createdAt: string;
    items: { title: string; quantity: number }[];
}

interface ScoutResponse {
    success: boolean;
    response: string;
    data: unknown;
    suggestedActions: { type: string; label: string; command: string }[];
}

interface Platform {
    id: string;
    name: string;
    icon: string;
    status: string;
    features: string[];
}

function CommandCenterPage() {
    const [activeTab, setActiveTab] = useState<'scout' | 'orders' | 'platforms'>('scout');
    const [scoutInput, setScoutInput] = useState('');
    const [scoutMessages, setScoutMessages] = useState<{ role: string; content: string }[]>([
        { role: 'assistant', content: "👋 I'm your AI Scout! Ask me anything about your business across all platforms. Try: \"Are there any due orders?\" or \"Show me this week's stats\"" }
    ]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [orders, setOrders] = useState<Order[]>([]);
    const [platforms, setPlatforms] = useState<Platform[]>([]);
    const [orderSyncError, setOrderSyncError] = useState<string | null>(null);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [stats, setStats] = useState({
        totalOrders: 0,
        dueOrders: 0,
        revenue: 0,
        unreadMessages: 0,
    });

    const scoutEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchPlatforms();
        fetchStats();
    }, []);

    useEffect(() => {
        scoutEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [scoutMessages]);

    const fetchPlatforms = async () => {
        try {
            const data = await apiFetch('/api/command-center/platforms');
            setPlatforms(data.platforms);
        } catch (error) {
            console.error('Failed to fetch platforms:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const data = await apiFetch('/api/command-center/stats?period=7d');
            setStats({
                totalOrders: data.stats?.totalOrders || 0,
                dueOrders: data.stats?.pendingFulfillment || 0,
                revenue: data.stats?.totalRevenue || 0,
                unreadMessages: 0,
            });
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const fetchOrders = async () => {
        setOrdersLoading(true);
        setOrderSyncError(null);
        try {
            const data = await apiFetch('/api/command-center/orders?status=open');
            setOrders(data.orders || []);
            if (data.errors?.length) {
                setOrderSyncError(data.errors.map((error: { platform?: string; error: string }) => `${error.platform || 'Store'}: ${error.error}`).join(' · '));
            }
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            setOrderSyncError(error instanceof Error ? error.message : 'Order synchronization failed');
        } finally {
            setOrdersLoading(false);
        }
    };

    const sendScoutCommand = async (e: FormEvent) => {
        e.preventDefault();
        if (!scoutInput.trim() || isProcessing) return;

        const userMessage = scoutInput.trim();
        setScoutMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setScoutInput('');
        setIsProcessing(true);

        try {
            const data: ScoutResponse = await apiFetch('/api/command-center/scout', {
                method: 'POST',
                body: JSON.stringify({ command: userMessage }),
            });

            setScoutMessages(prev => [...prev, {
                role: 'assistant',
                content: data.response || 'I processed your request but couldn\'t generate a response.'
            }]);

            if (data.suggestedActions?.length > 0) {
                setTimeout(() => {
                    toast.info(`Quick action: ${data.suggestedActions[0].label}`);
                }, 500);
            }
        } catch (error) {
            setScoutMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.'
            }]);
        } finally {
            setIsProcessing(false);
        }
    };

    const quickCommands = [
        { icon: '📦', label: 'Due Orders', command: 'Are there any due orders on all platforms?' },
        { icon: '💬', label: 'Messages', command: 'Check for unread messages across all platforms' },
        { icon: '📊', label: 'Weekly Stats', command: 'Show me this week\'s sales statistics' },
        { icon: '📈', label: 'Revenue', command: 'Give me a revenue breakdown by platform' },
        { icon: '☀️', label: 'Briefing', command: 'Give me my daily briefing' },
    ];

    const handleQuickCommand = (command: string) => {
        setScoutInput(command);
    };

    const getPlatformIcon = (platform: string) => {
        const icons: Record<string, string> = {
            shopify: '🛍️',
            etsy: '🧵',
            amazon: '📦',
            ebay: '🏷️',
            woocommerce: '🔮',
        };
        return icons[platform] || '🏪';
    };

    const connectPlatform = (platformId: string) => {
        if (platformId === 'shopify') {
            toast.info('Open Commerium from Shopify Admin → Apps to connect your store.');
            return;
        }

        // WooCommerce needs special handling - requires store URL
        if (platformId === 'woocommerce') {
            const storeUrl = prompt('Enter your WooCommerce store URL (e.g., https://mystore.com):');
            if (storeUrl) {
                apiFetch('/api/woocommerce/connect', {
                    method: 'POST',
                    body: JSON.stringify({ storeUrl }),
                })
                    .then(data => {
                        if (data.authUrl) {
                            window.location.href = data.authUrl;
                        } else {
                            toast.error(data.error || 'Failed to connect');
                        }
                    })
                    .catch(() => toast.error('Connection failed'));
            }
            return;
        }

        // Other platforms
        const connectUrls: Record<string, string> = {
            etsy: '/api/etsy/connect',
            ebay: '/api/ebay/connect',
            amazon: '/api/amazon/connect',
        };

        const url = connectUrls[platformId];
        if (url) {
            window.location.href = getApiUrl(url);
        } else {
            toast.info('This platform is coming soon!');
        }
    };

    return (
        <div className="command-center-page animate-fade-in">
            <h1 className="page-title">
                <span className="title-icon">🎯</span>
                Command Center
            </h1>
            <p className="page-subtitle">
                Unified dashboard across all your sales channels
            </p>

            <div className="stats-overview">
                <div className="stat-card highlight">
                    <span className="stat-icon">📦</span>
                    <div className="stat-info">
                        <span className="stat-value">{stats.dueOrders}</span>
                        <span className="stat-label">Due Orders</span>
                    </div>
                </div>
                <div className="stat-card">
                    <span className="stat-icon">💰</span>
                    <div className="stat-info">
                        <span className="stat-value">€{stats.revenue.toFixed(0)}</span>
                        <span className="stat-label">This Week</span>
                    </div>
                </div>
                <div className="stat-card">
                    <span className="stat-icon">🛒</span>
                    <div className="stat-info">
                        <span className="stat-value">{stats.totalOrders}</span>
                        <span className="stat-label">Orders</span>
                    </div>
                </div>
                <div className="stat-card">
                    <span className="stat-icon">💬</span>
                    <div className="stat-info">
                        <span className="stat-value">{stats.unreadMessages}</span>
                        <span className="stat-label">Messages</span>
                    </div>
                </div>
            </div>

            <div className="tab-nav">
                <button
                    className={`tab-btn ${activeTab === 'scout' ? 'active' : ''}`}
                    onClick={() => setActiveTab('scout')}
                >
                    🤖 AI Scout
                </button>
                <button
                    className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('orders'); fetchOrders(); }}
                >
                    📦 Orders
                </button>
                <button
                    className={`tab-btn ${activeTab === 'platforms' ? 'active' : ''}`}
                    onClick={() => setActiveTab('platforms')}
                >
                    🔌 Platforms
                </button>
            </div>

            {activeTab === 'scout' && (
                <div className="scout-section animate-slide-up">
                    <div className="quick-commands">
                        {quickCommands.map((cmd, i) => (
                            <button
                                key={i}
                                className="quick-cmd-btn"
                                onClick={() => handleQuickCommand(cmd.command)}
                            >
                                <span>{cmd.icon}</span>
                                {cmd.label}
                            </button>
                        ))}
                    </div>

                    <div className="scout-chat">
                        <div className="scout-messages">
                            {scoutMessages.map((msg, i) => (
                                <div key={i} className={`scout-message ${msg.role}`}>
                                    <div className="message-content">{msg.content}</div>
                                </div>
                            ))}
                            {isProcessing && (
                                <div className="scout-message assistant processing">
                                    <div className="typing-indicator">
                                        <span></span><span></span><span></span>
                                    </div>
                                </div>
                            )}
                            <div ref={scoutEndRef} />
                        </div>

                        <form className="scout-input-form" onSubmit={sendScoutCommand}>
                            <input
                                type="text"
                                value={scoutInput}
                                onChange={(e) => setScoutInput(e.target.value)}
                                placeholder="Ask about orders, messages, stats, or anything..."
                                disabled={isProcessing}
                            />
                            <button type="submit" disabled={isProcessing || !scoutInput.trim()}>
                                ➤
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {activeTab === 'orders' && (
                <div className="orders-section animate-slide-up">
                    <div className="orders-header">
                        <h3>All Orders</h3>
                        <div className="orders-filters">
                            <select defaultValue="all">
                                <option value="all">All Platforms</option>
                                <option value="shopify">Shopify</option>
                                <option value="etsy">Etsy</option>
                            </select>
                            <select defaultValue="pending">
                                <option value="pending">Pending</option>
                                <option value="shipped">Shipped</option>
                                <option value="all">All</option>
                            </select>
                        </div>
                    </div>

                    {orderSyncError && (
                        <div className="empty-state">
                            <span className="empty-icon">⚠️</span>
                            <p>Shopify order synchronization failed. No order count is being reported.</p>
                            <small>{orderSyncError}</small>
                        </div>
                    )}

                    {!orderSyncError && ordersLoading ? (
                        <div className="empty-state"><p>Synchronizing orders from Shopify…</p></div>
                    ) : !orderSyncError && orders.length > 0 ? (
                        <div className="orders-list">
                            {orders.map(order => (
                                <div key={order.id} className={`order-card ${order.needsAction ? 'needs-action' : ''}`}>
                                    <div className="order-header">
                                        <span className="platform-icon">{getPlatformIcon(order.platform)}</span>
                                        <span className="order-number">{order.orderNumber}</span>
                                        <span className={`order-status ${order.status}`}>{order.status}</span>
                                    </div>
                                    <div className="order-details">
                                        <span className="customer">{order.customerName}</span>
                                        <span className="total">{order.currency} {order.total.toFixed(2)}</span>
                                    </div>
                                    <div className="order-items">
                                        {order.items.slice(0, 2).map((item, i) => (
                                            <span key={i} className="item">{item.quantity}x {item.title}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !orderSyncError ? (
                        <div className="empty-state">
                            <span className="empty-icon">📦</span>
                            <p>{platforms.some(platform => platform.status === 'connected')
                                ? 'No open orders found on your connected platforms.'
                                : 'Connect a sales platform to synchronize orders.'}</p>
                        </div>
                    ) : null}
                </div>
            )}

            {activeTab === 'platforms' && (
                <div className="platforms-section animate-slide-up">
                    <div className="platforms-grid">
                        {platforms.map(platform => (
                            <div key={platform.id} className={`platform-card ${platform.status}`}>
                                <div className="platform-icon">{platform.icon}</div>
                                <div className="platform-info">
                                    <h4>{platform.name}</h4>
                                    <span className={`platform-status ${platform.status}`}>
                                        {platform.status === 'connected' ? '✓ Connected' :
                                            platform.status === 'coming_soon' ? '🔜 Coming Soon' :
                                                '+ Connect'}
                                    </span>
                                </div>
                                <div className="platform-features">
                                    {platform.features.slice(0, 3).map((f, i) => (
                                        <span key={i} className="feature-tag">{f}</span>
                                    ))}
                                </div>
                                {platform.status === 'available' && (
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => connectPlatform(platform.id)}
                                    >
                                        Connect
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default CommandCenterPage;
